import React from 'react';
import {
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    Button,
    makeStyles,
    Text,
    tokens,
} from '@fluentui/react-components';
import { Document } from '@si/fib-icons';
import { t } from 'i18next';
import { IDocumentDetails } from '../../../@types/models';
import { openDocumentLink } from '../../../utils/openDocumentLink';
import { ArchiveAgainMenu } from './ArchiveAgainMenu';

const ARCHIVE_CODE_IMPORT_ARCHIVE = 'Import archive';

const useStyles = makeStyles({
    root: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalM,
    },
    accordion: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXS,
    },
    item: {
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: tokens.borderRadiusMedium,
    },
    headerContent: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        minWidth: 0,
    },
    headerText: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
    },
    meta: {
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase100,
    },
    details: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
        paddingBottom: tokens.spacingVerticalS,
    },
    detail: {
        display: 'flex',
        flexDirection: 'column',
    },
    detailValue: {
        color: tokens.colorNeutralForeground2,
    },
    panelActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: tokens.spacingVerticalS,
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
});

interface MultipleArchiveDetailProps {
    documents: IDocumentDetails[];
}

export const MultipleArchiveDetail: React.FC<MultipleArchiveDetailProps> = ({ documents }) => {
    const styles = useStyles();

    return (
        <div className={styles.root}>
            <Accordion className={styles.accordion} collapsible multiple defaultOpenItems={['document-0']}>
                {documents.map((document, index) => {
                    const itemValue = `document-${index}`;
                    const documentNumber = document.Documentnumber || document.DocumentNumber;
                    const responsible = [document.ResponsibleName, document.OrgUnitName].filter(Boolean).join(' - ');
                    const accessCode = [document.AccessCodeDesc, document.Paragraph].filter(Boolean).join(' / ');

                    return (
                        <AccordionItem className={styles.item} key={`${document.DocumentRecno ?? documentNumber}-${index}`} value={itemValue}>
                            <AccordionHeader>
                                <div className={styles.headerContent}>
                                    <Document size={16} />
                                    <div className={styles.headerText}>
                                        <Text weight="semibold">{document.DocumentTitle}</Text>
                                        <Text className={styles.meta}>{[document.ArchiveCode, documentNumber].filter(Boolean).join(' : ')}</Text>
                                    </div>
                                </div>
                            </AccordionHeader>
                            <AccordionPanel>
                                <div className={styles.details}>
                                    {document.CaseNumber && document.CaseDescription && (
                                        <div className={styles.detail}>
                                            <Text weight="semibold">{t('Case')}</Text>
                                            <Text className={styles.detailValue}>{`${document.CaseNumber} ${document.CaseDescription}`}</Text>
                                        </div>
                                    )}
                                    <div className={styles.detail}>
                                        <Text weight="semibold">{t('Document Archive')}</Text>
                                        <Text className={styles.detailValue}>{document.ArchiveCode}</Text>
                                    </div>
                                    <div className={styles.detail}>
                                        <Text weight="semibold">{t('Document category')}</Text>
                                        <Text className={styles.detailValue}>{document.DocumentCategory || document.CategoryCode}</Text>
                                    </div>
                                    <div className={styles.detail}>
                                        <Text weight="semibold">{t('Responsible person')}</Text>
                                        <Text className={styles.detailValue}>{responsible}</Text>
                                    </div>
                                    <div className={styles.detail}>
                                        <Text weight="semibold">{t('Access code')}</Text>
                                        <Text className={styles.detailValue}>{accessCode}</Text>
                                    </div>
                                    <div className={styles.panelActions}>
                                        <Button
                                            appearance="primary"
                                            aria-label={t('Open or register')}
                                            onClick={() => openDocumentLink(document.DocumentDetailsLink, document.DocumentRecno, document.ArchiveCode)}
                                        >
                                            {document.ArchiveCode === ARCHIVE_CODE_IMPORT_ARCHIVE ? t('Register') : t('Open document in 360')}
                                        </Button>
                                    </div>
                                </div>
                            </AccordionPanel>
                        </AccordionItem>
                    );
                })}
            </Accordion>
            <div className={styles.actions}>
                <ArchiveAgainMenu />
            </div>
        </div>
    );
};